import type {
  AddEdgeInput,
  EnumDetail,
  PendingChange,
  PendingChangesFilter,
  SchemaInfo,
  SchemaNodeInfo,
  TableDetail,
  TableFilter,
  TableInfo,
  UpdateColumnReviewInput,
  UpdateTableReviewInput,
  UpsertEnumInput,
} from '../../types/db-dictionary.js';
import { request } from './_request.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DB辞書APIクライアント。getSchemas/getTables/getTableDetail/updateTableReview/updateColumnReviewメソッドでDB辞書エンドポイントを呼び出し
 * @graph-connects /db-dictionary/schemas [calls] スキーマ一覧API
 * @graph-connects /db-dictionary/schemas/:schema/tables [calls] テーブル一覧API
 * @graph-connects /db-dictionary/schemas/:schema/tables/:table [calls] テーブル詳細・更新API
 * @graph-connects /db-dictionary/schemas/:schema/tables/:table/columns/:column [calls] カラムレビュー更新API
 */
export const dbDictionaryApi = {
  getSchemas: async (): Promise<SchemaInfo[]> => {
    const res = await request<{ schemas: SchemaInfo[] }>('/db-dictionary/schemas');
    return res.schemas;
  },
  getTables: async (
    schema: string,
    filter?: TableFilter,
  ): Promise<{ schema: SchemaNodeInfo | null; tables: TableInfo[] }> => {
    const query = new URLSearchParams();
    if (filter && filter !== 'all') query.set('filter', filter);
    const queryString = query.toString();
    return request<{ schema: SchemaNodeInfo | null; tables: TableInfo[] }>(
      `/db-dictionary/schemas/${encodeURIComponent(schema)}/tables${queryString ? `?${queryString}` : ''}`,
    );
  },
  updateSchemaDescription: (schema: string, data: UpdateTableReviewInput): Promise<unknown> => {
    return request(`/db-dictionary/schemas/${encodeURIComponent(schema)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  getTableDetail: async (schema: string, table: string, locale?: string): Promise<TableDetail> => {
    const query = locale ? `?locale=${locale}` : '';
    return request<TableDetail>(
      `/db-dictionary/schemas/${encodeURIComponent(schema)}/tables/${encodeURIComponent(table)}${query}`,
    );
  },
  updateTableReview: (
    schema: string,
    table: string,
    data: UpdateTableReviewInput,
  ): Promise<TableDetail> => {
    return request<TableDetail>(
      `/db-dictionary/schemas/${encodeURIComponent(schema)}/tables/${encodeURIComponent(table)}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    );
  },
  updateColumnReview: (
    schema: string,
    table: string,
    column: string,
    data: UpdateColumnReviewInput,
  ): Promise<TableDetail> => {
    return request<TableDetail>(
      `/db-dictionary/schemas/${encodeURIComponent(schema)}/tables/${encodeURIComponent(table)}/columns/${encodeURIComponent(column)}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    );
  },
  invalidateCache: (): Promise<{ success: boolean; deletedKeys: number }> => {
    return request<{ success: boolean; deletedKeys: number }>('/db-dictionary/cache/invalidate', {
      method: 'POST',
    });
  },
  getPendingChanges: async (
    filter?: PendingChangesFilter,
  ): Promise<{ changes: PendingChange[] }> => {
    const query = new URLSearchParams();
    if (filter && filter !== 'pending') query.set('filter', filter);
    const queryString = query.toString();
    return request<{ changes: PendingChange[] }>(
      `/db-dictionary/changes${queryString ? `?${queryString}` : ''}`,
    );
  },
  reviewChange: (qualifiedName: string): Promise<{ success: boolean }> => {
    return request<{ success: boolean }>(
      `/db-dictionary/changes/${encodeURIComponent(qualifiedName)}`,
      { method: 'PUT' },
    );
  },
  /** エッジ追加 (FK / SAME_ENTITY / USES_ENUM) */
  addEdge: (input: AddEdgeInput): Promise<{ created: boolean; edgeId: string }> => {
    return request<{ created: boolean; edgeId: string }>('/db-dictionary/edges', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /** エッジ削除 */
  deleteEdge: (edgeId: string): Promise<{ success: boolean; tableQN: string | null }> => {
    return request<{ success: boolean; tableQN: string | null }>(
      `/db-dictionary/edges/${encodeURIComponent(edgeId)}`,
      { method: 'DELETE' },
    );
  },
  /** Enum 詳細取得 (接続カラム含む) */
  getEnumDetail: (enumName: string): Promise<EnumDetail> => {
    return request<EnumDetail>(`/db-dictionary/enums/${encodeURIComponent(enumName)}`);
  },
  /** Enum upsert */
  upsertEnum: (input: UpsertEnumInput): Promise<{ created: boolean; nodeId: string }> => {
    return request<{ created: boolean; nodeId: string }>(
      `/db-dictionary/enums/${encodeURIComponent(input.name)}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      },
    );
  },
};
